import React, { useEffect, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import useMediaQuery from "@mui/material/useMediaQuery";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FONTS, LANDING_COMPACT_NAV, LANDING_MOBILE } from "./landingTheme";

/* ─────────────────────── NAVBAR ─────────────────────── */
const Navbar = ({ colors, darkMode, setDarkMode, onOpenAuth, onOpenContact, onNavigate }) => {
    const navigate = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);
    const headerRef = useRef(null);
    // Celular: logo e botões menores. Até tablet a pílula de links não cabe e vira o menu do botão ☰
    const isMobile = useMediaQuery(LANDING_MOBILE);
    const isCompact = useMediaQuery(LANDING_COMPACT_NAV);
    const navLinks = [
        { label: "Soluções", onClick: () => onNavigate("solucoes") },
        { label: "Como Funciona", onClick: () => onNavigate("como-funciona") },
        { label: "Integrações", onClick: () => onNavigate("integracoes") },
        { label: "Preços", onClick: () => onNavigate("precos") },
        { label: "Contato", onClick: onOpenContact },
    ];
    const pickFromMenu = (action) => { setMenuOpen(false); action(); };

    // Menu do celular fecha ao tocar fora, com Esc e quando a tela volta a ter a pílula
    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = (e) => { if (!headerRef.current?.contains(e.target)) setMenuOpen(false); };
        const onKeyDown = (e) => { if (e.key === "Escape") setMenuOpen(false); };
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen]);
    useEffect(() => { if (!isCompact) setMenuOpen(false); }, [isCompact]);

    const headerTop = isMobile ? 12 : 24;
    const roundButtonStyle = {
        width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius: "50%", border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
        background: darkMode ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.85)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
    };
    const panelStyle = {
        padding: 8, borderRadius: 24, boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
        background: darkMode ? "rgba(11,17,32,0.95)" : "rgba(255,255,255,0.97)", backdropFilter: "blur(20px)",
        border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
    };
    const hoverOn = (e) => { e.currentTarget.style.background = darkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; };
    const hoverOff = (e) => { e.currentTarget.style.background = "transparent"; };

    // =========================================================================
    // ⚙️ CONFIGURAÇÕES DE POSICIONAMENTO DA LOGO (CALIBRAÇÃO VISUAL)
    // Altere estes valores para mover a logo na horizontal (X) ou vertical (Y)
    const LOGO_X_OFFSET = 0;  // Ajuste horizontal (distância da borda esquerda em px)
    const LOGO_Y_OFFSET = 0;   // Ajuste vertical (valores positivos descem, negativos sobem)
    // =========================================================================

    return (
        <motion.header
            ref={headerRef}
            initial={{ y: -100, x: "-50%" }}
            animate={{ y: 0, x: "-50%" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            style={{
                position: "fixed",
                top: headerTop,
                left: "50%",
                zIndex: 100,
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr auto" : "1fr auto 1fr",
                alignItems: "center",
                width: isMobile ? "calc(100% - 24px)" : "calc(100% - 48px)",
                maxWidth: 1300, // <--- Ajustado para 1300
                boxSizing: "border-box",
            }}
        >
            {/* Lado Esquerdo: Logo da EM */}
            <div
                onClick={() => navigate("/")}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    justifySelf: "start",
                    cursor: "pointer",
                    transform: `translate(${LOGO_X_OFFSET}px, ${LOGO_Y_OFFSET}px)` // Usado para calibração fina
                }}
            >
                {/* ──────────────── TAMANHO DO LOGO ──────────────── */}
                <svg viewBox="7.44 2.64 15.84 10.08" width={isMobile ? 64 : 94} height={isMobile ? 41 : 60} fill={colors.primaryMain}>
                    <g>
                        <rect x="13.2" y="5.52" width="1.44" height="1.44" />
                        <rect x="11.76" y="5.52" width="1.44" height="1.44" />
                        <rect x="10.32" y="5.52" width="1.44" height="1.44" />
                        <rect x="8.88" y="5.52" width="1.44" height="1.44" />
                        <rect x="7.44" y="5.52" width="6.95" height="1.44" />
                        <rect x="13.2" y="2.64" width="1.44" height="1.44" />
                        <rect x="11.76" y="2.64" width="1.44" height="1.44" />
                        <rect x="10.32" y="2.64" width="1.44" height="1.44" />
                        <rect x="8.88" y="2.64" width="1.44" height="1.44" />
                        <rect x="7.44" y="2.64" width="7.2" height="1.44" />
                        <rect x="21.84" y="11.28" width="1.44" height="1.44" />
                        <rect x="7.44" y="11.28" width="15.84" height="1.44" />
                        <rect x="7.44" y="6.34" width="1.44" height="2.51" />
                        <rect x="7.44" y="8.4" width="6.39" height="1.44" />
                        <rect x="8.88" y="8.4" width="1.44" height="1.44" />
                        <rect x="10.32" y="8.4" width="1.44" height="1.44" />
                        <rect x="11.76" y="8.4" width="1.44" height="1.44" />
                        <rect x="13.2" y="8.4" width="1.44" height="1.44" />
                        <rect x="16.08" y="5.52" width="1.44" height="1.44" />
                        <rect x="16.08" y="6.96" width="1.44" height="1.44" />
                        <rect x="16.08" y="8.4" width="1.44" height="1.44" />
                        <rect x="18.96" y="5.52" width="1.44" height="3.6" />
                        <rect x="18.96" y="6.96" width="1.44" height="1.44" />
                        <rect x="18.96" y="8.4" width="1.44" height="1.44" />
                        <rect x="16.08" y="4.08" width="1.44" height="1.44" />
                        <rect x="16.08" y="2.64" width="1.44" height="6.3" />
                        <rect x="21.84" y="5.52" width="1.44" height="1.44" />
                        <rect x="21.84" y="6.96" width="1.44" height="1.44" />
                        <rect x="21.84" y="3.69" width="1.44" height="6.15" />
                        <rect x="21.84" y="4.08" width="1.44" height="1.44" />
                        <rect x="16.94" y="2.64" width="6.34" height="1.44" />
                        <rect x="17.52" y="2.64" width="1.44" height="1.44" />
                        <rect x="18.96" y="2.64" width="1.44" height="1.44" />
                        <rect x="20.4" y="2.64" width="1.44" height="1.44" />
                    </g>
                </svg>
            </div>

            {/* Centro: Pílula mais "gordinha" e com links levemente maiores */}
            <nav style={{
                display: isCompact && !isMobile ? "none" : "flex", alignItems: "center", gap: isMobile ? 0 : 4,
                justifyContent: isMobile ? "space-between" : undefined, boxSizing: "border-box",
                gridColumn: isMobile ? "1 / -1" : undefined, gridRow: isMobile ? 2 : undefined,
                width: isMobile ? "100%" : undefined, marginTop: isMobile ? 8 : undefined,
                padding: isMobile ? "4px 6px" : "8px 12px", // Mais padding ao redor
                background: darkMode ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.85)", backdropFilter: "blur(20px)",
                borderRadius: 60, border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)",
            }}>
                {navLinks.map((link) => (
                    <div
                        key={link.label}
                        style={{ position: "relative" }}
                    >
                        <button
                            type="button"
                            onClick={link.onClick}
                            style={{
                                fontFamily: FONTS.body, fontSize: isMobile ? 11.5 : 15, fontWeight: 600, color: colors.textDark, background: "transparent", border: "none",
                                padding: isMobile ? "9px 5px" : "10px 18px", cursor: "pointer", borderRadius: 30, // Links com mais área de clique
                                display: "inline-flex", alignItems: "center", gap: 4, transition: "background 0.2s",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                            {link.label}
                        </button>

                    </div>
                ))}
            </nav>

            {/* Lado Direito: Botões */}
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12, justifySelf: "end" }}>
                <motion.button
                    onClick={() => setDarkMode(!darkMode)}
                    aria-label={darkMode ? "Mudar para o tema claro" : "Mudar para o tema escuro"}
                    whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
                    style={roundButtonStyle}
                >
                    {darkMode ? <LightModeRoundedIcon style={{ fontSize: 22, color: colors.textDark }} /> : <DarkModeRoundedIcon style={{ fontSize: 22, color: colors.textDark }} />}
                </motion.button>
                <motion.button
                    onClick={onOpenAuth}
                    whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }}
                    style={{
                        fontFamily: FONTS.body, fontSize: isMobile ? 15 : 16, fontWeight: 700, color: colors.white,
                        background: colors.purpleDark, border: "none", borderRadius: 50,
                        padding: isMobile ? "10px 22px" : "14px 32px", cursor: "pointer",
                    }}
                >
                    Entrar
                </motion.button>
                {isCompact && !isMobile && (
                    <motion.button
                        type="button"
                        onClick={() => setMenuOpen((o) => !o)}
                        aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
                        aria-expanded={menuOpen}
                        aria-controls="landing-menu"
                        whileTap={{ scale: 0.95 }}
                        style={roundButtonStyle}
                    >
                        {menuOpen
                            ? <CloseRoundedIcon style={{ fontSize: 22, color: colors.textDark }} />
                            : <MenuRoundedIcon style={{ fontSize: 22, color: colors.textDark }} />}
                    </motion.button>
                )}
            </div>

            {/* Menu do celular/tablet: os mesmos links da pílula */}
            <AnimatePresence>
                {isCompact && !isMobile && menuOpen && (
                    <motion.nav
                        id="landing-menu"
                        aria-label="Menu"
                        initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}
                        style={{
                            position: "absolute", top: "calc(100% + 10px)", left: 0, right: 0, ...panelStyle,
                            maxHeight: `calc(100dvh - ${headerTop + 80}px)`, overflowY: "auto", overscrollBehavior: "contain",
                        }}
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                type="button"
                                onClick={() => pickFromMenu(link.onClick)}
                                style={{
                                    display: "flex", alignItems: "center", width: "100%", minHeight: 48, padding: "12px 14px", borderRadius: 16,
                                    border: "none", background: "transparent", cursor: "pointer", textAlign: "left",
                                    fontFamily: FONTS.body, fontSize: 16, fontWeight: 600, color: colors.textDark,
                                }}
                                onMouseEnter={hoverOn}
                                onMouseLeave={hoverOff}
                            >
                                {link.label}
                            </button>
                        ))}
                    </motion.nav>
                )}
            </AnimatePresence>
        </motion.header>
    );
};

export default Navbar;

